import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline"
import React, { useState, useEffect } from "react"
import { useMutation } from "urql"

import { Button } from "../../../../components/shared/Buttons"
import { FilePicker } from "../../../../components/shared/FileStackWidgets"
import {
  InstagramIcon,
  TikTokIcon,
  XIcon,
  FacebookIcon,
  YouTubeIcon,
  LinkedInIcon
} from "../../../../components/shared/icons/SocialIcons"
import { Input, Select, Toggle } from "../../../../components/shared/Inputs"
import Typography from "../../../../components/shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

const FooterForm = ({ theme, practice, onUpdate, onClose }) => {
  const { pages } = useWebsiteBuilderV2()
  const [logo, setLogo] = useState(theme?.footer?.logo || null)
  const [logoSize, setLogoSize] = useState(theme?.footer?.logoSize || 50)
  const [ctaType, setCtaType] = useState(theme?.footer?.ctaType || "page")
  const [buttonText, setButtonText] = useState(theme?.footer?.buttonText || "Book Now")
  const [page, setPage] = useState(theme?.footer?.page || "services")
  const [service, setService] = useState(theme?.footer?.service || "")
  const [disclaimerText, setDisclaimerText] = useState(theme?.footer?.disclaimerText || "")
  const [ctaButtonBackground, setCtaButtonBackground] = useState(
    theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF"
  )
  const [ctaButtonText, setCtaButtonText] = useState(
    theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f"
  )
  const [showHome, setShowHome] = useState(theme?.footer?.showHome || false)

  // Social links state
  const [socialLinks, setSocialLinks] = useState({
    instagram: theme?.footer?.socialLinks?.instagram || "",
    tiktok: theme?.footer?.socialLinks?.tiktok || "",
    x: theme?.footer?.socialLinks?.x || "",
    facebook: theme?.footer?.socialLinks?.facebook || "",
    youtube: theme?.footer?.socialLinks?.youtube || "",
    linkedin: theme?.footer?.socialLinks?.linkedin || ""
  })

  // GraphQL mutation for uploading images
  const UPLOAD_IMAGE_MUTATION = `
    mutation UploadImage($handle: String!, $description: String!, $provider: String) {
      uploadImage(handle: $handle, description: $description, provider: $provider) {
        result
        errors
        photoUrl
        altText
      }
    }
  `
  const [{ fetching: uploadFetching }, uploadImage] = useMutation(UPLOAD_IMAGE_MUTATION)
  const { showToast } = useToast()

  // Dynamic page options from context
  const pageOptions = pages
    ?.filter((page) => page.slug !== "home") // Exclude home page
    ?.map((page) => ({
      value: page.slug,
      label: page.title
    })) || [
    { value: "services", label: "Services" },
    { value: "about", label: "About" },
    { value: "contact", label: "Contact" }
  ]

  // Service options from practice
  const serviceOptions =
    practice?.services?.map((service) => ({
      value: service.id.toString(),
      label: service.name
    })) || []

  // Sync form state with theme prop when it changes
  useEffect(() => {
    setLogo(theme?.footer?.logo || null)
    setLogoSize(theme?.footer?.logoSize || 50)
    setCtaType(theme?.footer?.ctaType || "page")
    setButtonText(theme?.footer?.buttonText || "Book Now")
    setPage(theme?.footer?.page || "services")
    setService(theme?.footer?.service || "")
    setDisclaimerText(theme?.footer?.disclaimerText || "")
    setCtaButtonBackground(theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF")
    setCtaButtonText(theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f")
    setShowHome(theme?.footer?.showHome || false)
    setSocialLinks({
      instagram: theme?.footer?.socialLinks?.instagram || "",
      tiktok: theme?.footer?.socialLinks?.tiktok || "",
      x: theme?.footer?.socialLinks?.x || "",
      facebook: theme?.footer?.socialLinks?.facebook || "",
      youtube: theme?.footer?.socialLinks?.youtube || "",
      linkedin: theme?.footer?.socialLinks?.linkedin || ""
    })
  }, [theme])

  // Auto-select first option when ctaType changes
  useEffect(() => {
    if (ctaType === "page" && pageOptions.length > 0) {
      setPage(pageOptions[0].value)
    } else if (ctaType === "service" && serviceOptions.length > 0) {
      setService(serviceOptions[0].value)
    }
  }, [ctaType])

  // Apply temporary preview when values change
  useEffect(() => {
    const footerData = {
      footer: {
        logo,
        logoSize,
        ctaType,
        buttonText,
        page,
        service,
        disclaimerText,
        ctaButtonBackground,
        ctaButtonText,
        showHome,
        socialLinks,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? service : page
      }
    }
    onUpdate(footerData, { temporary: true })
  }, [
    logo,
    logoSize,
    ctaType,
    buttonText,
    page,
    service,
    disclaimerText,
    ctaButtonBackground,
    ctaButtonText,
    showHome,
    socialLinks
  ])

  // Handle logo upload
  const handleLogoUpload = (res) => {
    console.log("FilePicker response:", res) // eslint-disable-line no-console

    const file = res.filesUploaded[0]
    console.log("File data:", file) // eslint-disable-line no-console

    uploadImage({
      handle: file.handle,
      description: "footer_logo",
      provider: "filestack"
    })
      .then((result) => {
        console.log("Upload result:", result) // eslint-disable-line no-console

        if (result?.data?.uploadImage?.result === "success") {
          setLogo(result.data.uploadImage.photoUrl)
          showToast("Logo uploaded successfully!")
        } else {
          console.error("Upload failed:", result) // eslint-disable-line no-console
          let errorMessage = "There was an error uploading your logo. Please try again later or contact support."
          if (result.data?.uploadImage?.errors) errorMessage += ` ${result.data.uploadImage.errors}`
          showToast({
            type: "error",
            content: errorMessage
          })
        }
      })
      .catch((error) => {
        console.error("Upload error:", error) // eslint-disable-line no-console
        showToast({
          type: "error",
          content: "There was an error uploading your logo. Please try again later or contact support."
        })
      })
  }

  // Handle logo removal
  const handleLogoRemove = () => {
    setLogo(null)
    showToast("Logo removed.")
  }

  // Handle social link changes
  const handleSocialLinkChange = (platform, value) => {
    setSocialLinks((prev) => ({
      ...prev,
      [platform]: value
    }))
  }

  const handleSave = () => {
    const saveData = {
      footer: {
        logo,
        logoSize,
        ctaType,
        buttonText,
        page,
        service,
        disclaimerText,
        ctaButtonBackground,
        ctaButtonText,
        showHome,
        socialLinks,
        // Map form data to the correct settings structure
        ctaAction: ctaType,
        ctaTarget: ctaType === "service" ? service : page
      }
    }
    onUpdate(saveData)
    onClose()
  }

  const handleClose = () => {
    // Revert to stored theme when closing without saving
    const storedCtaType = theme?.footer?.ctaType || "page"
    const storedPage = theme?.footer?.page || "services"
    const storedService = theme?.footer?.service || ""
    const storedSocialLinks = theme?.footer?.socialLinks || {
      instagram: "",
      tiktok: "",
      x: "",
      facebook: "",
      youtube: "",
      linkedin: ""
    }

    const storedFooterData = {
      footer: {
        logo: theme?.footer?.logo || null,
        logoSize: theme?.footer?.logoSize || 50,
        ctaType: storedCtaType,
        buttonText: theme?.footer?.buttonText || "Book Now",
        page: storedPage,
        service: storedService,
        disclaimerText: theme?.footer?.disclaimerText || "",
        ctaButtonBackground: theme?.footer?.ctaButtonBackground || theme?.colors?.buttonBackground || "#FFFFFF",
        ctaButtonText: theme?.footer?.ctaButtonText || theme?.colors?.textDark || "#55433f",
        showHome: theme?.footer?.showHome || false,
        socialLinks: storedSocialLinks,
        // Map form data to the correct settings structure
        ctaAction: storedCtaType,
        ctaTarget: storedCtaType === "service" ? storedService : storedPage
      }
    }
    onUpdate(storedFooterData, { temporary: true })
    onClose()
  }

  return (
    <div className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
      {/* Header Title and Close */}
      <div className="flex items-center justify-between border-b border-gray-light px-8 py-6">
        <Typography variant="h4" className="font-bold text-gray-dark">
          Footer
        </Typography>
        <button onClick={handleClose} className="rounded p-1 transition-colors hover:bg-gray-light">
          <XMarkIcon className="h-6 w-6 text-gray" />
        </button>
      </div>

      {/* Logo Upload */}
      <div className="px-8 pt-6">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Logo
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Select an image to feature in this section.
        </Typography>

        {/* Logo Preview */}
        <div className="mb-6 flex items-center justify-center">
          <div
            className="flex items-center justify-center rounded-lg border border-gray-200 bg-blue-50 p-4"
            style={{
              width: `${logoSize * 2}px`,
              height: `${logoSize * 1.2}px`,
              minWidth: "80px",
              minHeight: "48px"
            }}>
            {logo ? (
              <img
                src={logo}
                alt="Logo"
                className="max-h-full max-w-full object-contain"
                style={{ maxWidth: `${logoSize}px` }}
              />
            ) : (
              <Typography variant="body" className="font-medium text-blue-600">
                {practice?.name || ""}
              </Typography>
            )}
          </div>
        </div>

        {/* Logo Upload Area */}
        {logo ? (
          <div className="mb-6 flex items-center justify-center">
            <div className="flex items-center gap-4">
              <img
                src={logo}
                alt="Footer logo"
                className="h-16 w-16 rounded-lg object-contain"
                style={{ maxWidth: `${logoSize}px` }}
              />
              <div className="flex flex-col gap-2">
                <FilePicker
                  as="button"
                  className="font-bold text-teal"
                  onUploadDone={handleLogoUpload}
                  disabled={uploadFetching}
                  transformations={{}}>
                  {uploadFetching ? "Uploading..." : "Edit logo"}
                </FilePicker>
                <button className="font-bold text-red" onClick={handleLogoRemove} disabled={uploadFetching}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : (
          <FilePicker
            as="button"
            onUploadDone={handleLogoUpload}
            disabled={uploadFetching}
            transformations={{}}
            className="mb-6 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-ultralight py-6 text-teal">
            <ArrowUpTrayIcon className="mb-2 h-6 w-6" />
            <Typography variant="body" className="mb-1">
              {uploadFetching ? "Uploading..." : "Drag 'n' drop or upload an image"}
            </Typography>
            <Typography variant="small" className="text-gray">
              Supported formats: PNG, JPG, SVG
            </Typography>
          </FilePicker>
        )}
      </div>

      {/* Logo Size Slider */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Logo size
        </Typography>
        <Typography variant="small" className="mb-3 text-gray">
          Use the slider to adjust the logo size within the footer.
        </Typography>
        <div className="flex items-center space-x-3">
          <input
            type="range"
            min="72"
            max="150"
            value={logoSize}
            onChange={(e) => setLogoSize(parseInt(e.target.value))}
            className="h-2 flex-1 cursor-pointer appearance-none rounded-lg bg-gray-200 slider"
          />
          <Typography variant="small" className="min-w-[3rem] font-medium text-gray-dark">
            {logoSize}px
          </Typography>
        </div>
      </div>

      {/* Navigation Settings */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Navigation
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Configure which navigation links to show in the footer.
        </Typography>
        <div className="mb-6 flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="body" className="font-medium text-gray-dark">
              Show [Home] link
            </Typography>
            <Typography variant="small" className="text-gray">
              Include a [Home] link in the navigation menu
            </Typography>
          </div>
          <div className="ml-4">
            <Toggle checked={showHome} onChange={setShowHome} />
          </div>
        </div>
      </div>

      {/* CTA Button Section */}
      <div className="px-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Call-to-action button
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Guide visitors to the next step with a clear call-to-action button.
        </Typography>
        <div className="mb-4 flex items-center space-x-6">
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="page"
              checked={ctaType === "page"}
              onChange={() => setCtaType("page")}
              className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Page</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="radio"
              name="ctaType"
              value="service"
              checked={ctaType === "service"}
              onChange={() => setCtaType("service")}
              className="h-5 w-5 border-gray-300 text-teal form-radio focus:ring-teal"
            />
            <span className="font-medium text-gray-dark">Service</span>
          </label>
        </div>
        <Input
          label="Button text"
          value={buttonText}
          onChange={(e) => setButtonText(e.target.value)}
          className="mb-4"
        />
        {ctaType === "page" ? (
          <Select
            label="Page"
            value={page}
            onChange={(e) => setPage(e.target.value)}
            options={pageOptions}
            className="mb-6"
          />
        ) : (
          <Select
            label="Service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            options={serviceOptions}
            placeholder="Select a service"
            className="mb-6"
          />
        )}

        {/* Button Colors */}
        <div className="mb-8 space-y-4">
          <Typography variant="title" className="text-gray-dark">
            Button colors
          </Typography>

          {/* Background Color */}
          <div>
            <Typography variant="small" className="mb-2 font-medium text-gray-dark">
              Background color
            </Typography>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={ctaButtonBackground}
                  onChange={(e) => setCtaButtonBackground(e.target.value)}
                  className="h-10 w-20 cursor-pointer rounded-lg border-2 border-gray-300 transition-all hover:border-teal"
                  style={{ backgroundColor: ctaButtonBackground }}
                />
              </div>
              <input
                type="text"
                value={ctaButtonBackground}
                onChange={(e) => setCtaButtonBackground(e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal"
              />
            </div>
          </div>

          {/* Text Color */}
          <div>
            <Typography variant="small" className="mb-2 font-medium text-gray-dark">
              Text color
            </Typography>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="color"
                  value={ctaButtonText}
                  onChange={(e) => setCtaButtonText(e.target.value)}
                  className="h-10 w-20 cursor-pointer rounded-lg border-2 border-gray-300 transition-all hover:border-teal"
                  style={{ backgroundColor: ctaButtonText }}
                />
              </div>
              <input
                type="text"
                value={ctaButtonText}
                onChange={(e) => setCtaButtonText(e.target.value)}
                placeholder="#55433f"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Text Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Footer text (optional)
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Add any text that you want to include in the footer.
        </Typography>
        <textarea
          value={disclaimerText}
          onChange={(e) => setDisclaimerText(e.target.value)}
          placeholder="Enter footer text here..."
          className="h-32 w-full resize-none rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal"
        />
      </div>

      {/* Social Links Section */}
      <div className="px-8 pb-8">
        <Typography variant="h5" className="mb-1 font-bold text-gray-dark">
          Social Links
        </Typography>
        <Typography variant="small" className="mb-4 text-gray">
          Add your social media links. Icons will only appear if you provide a URL.
        </Typography>

        <div className="space-y-4">
          {/* Instagram */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100">
              <InstagramIcon className="h-5 w-5 text-pink-600" />
            </div>
            <Input
              label="Instagram"
              value={socialLinks.instagram}
              onChange={(e) => handleSocialLinkChange("instagram", e.target.value)}
              placeholder="https://instagram.com/yourusername"
              className="flex-1"
            />
          </div>

          {/* TikTok */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black">
              <TikTokIcon className="h-5 w-5 text-white" />
            </div>
            <Input
              label="TikTok"
              value={socialLinks.tiktok}
              onChange={(e) => handleSocialLinkChange("tiktok", e.target.value)}
              placeholder="https://tiktok.com/@yourusername"
              className="flex-1"
            />
          </div>

          {/* X (Twitter) */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black">
              <XIcon className="h-5 w-5 text-white" />
            </div>
            <Input
              label="X (Twitter)"
              value={socialLinks.x}
              onChange={(e) => handleSocialLinkChange("x", e.target.value)}
              placeholder="https://x.com/yourusername"
              className="flex-1"
            />
          </div>

          {/* Facebook */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
              <FacebookIcon className="h-5 w-5 text-white" />
            </div>
            <Input
              label="Facebook"
              value={socialLinks.facebook}
              onChange={(e) => handleSocialLinkChange("facebook", e.target.value)}
              placeholder="https://facebook.com/yourpage"
              className="flex-1"
            />
          </div>

          {/* YouTube */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600">
              <YouTubeIcon className="h-5 w-5 text-white" />
            </div>
            <Input
              label="YouTube"
              value={socialLinks.youtube}
              onChange={(e) => handleSocialLinkChange("youtube", e.target.value)}
              placeholder="https://youtube.com/@yourchannel"
              className="flex-1"
            />
          </div>

          {/* LinkedIn */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-700">
              <LinkedInIcon className="h-5 w-5 text-white" />
            </div>
            <Input
              label="LinkedIn"
              value={socialLinks.linkedin}
              onChange={(e) => handleSocialLinkChange("linkedin", e.target.value)}
              placeholder="https://linkedin.com/in/yourprofile"
              className="flex-1"
            />
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <Button type="secondary" onClick={handleClose} className="px-6">
          Close
        </Button>
        <Button type="primary" onClick={handleSave} className="px-6">
          Save
        </Button>
      </div>
    </div>
  )
}

export default FooterForm
